import { Injectable } from '@angular/core';
import { environment } from '../../environments/environment';

/**
 * Theme tokens applied on document.documentElement so SCSS across the app
 * can use var(--app-primary), var(--app-accent), etc.
 */
export interface AccountThemeTokens {
  primary: string;
  /** Darker shade for hovers / contrast */
  primaryDark?: string;
  /** Secondary brand (navbar accents, links) */
  accent?: string;
  /** "r, g, b" for legacy rgba(var(--app-primary-rgb), alpha) patterns */
  primaryRgb?: string;
}

/** Navbar + sidebar surfaces (optional per account). */
export interface AccountShellTokens {
  navbarBgStart: string;
  navbarBgEnd: string;
  navbarFg: string;
  navbarFgSecondary: string;
  navbarFgTertiary: string;
  navbarIconMuted: string;
  dropdownBg: string;
  dropdownBorder: string;
  dropdownHeaderBorder: string;
  dropdownDivider: string;
  sidebarBg: string;
  sidebarText: string;
  sidebarTextMuted: string;
  sidebarBorder: string;
  sidebarHover: string;
  sidebarActive: string;
  sidebarIcon: string;
  sidebarSearchBg: string;
  sidebarSearchBorder: string;
  sidebarPopupHeaderBg: string;
}

/** Custom table (`app-custom-table-view`) surface tokens. */
export interface CustomTableThemeTokens {
  headerBg: string;
  headerFg: string;
  headerIcon: string;
  headerDivider: string;
  rowBg: string;
  rowAltBg: string;
  rowHover: string;
  rowDivider: string;
  bodyText: string;
  bodyTextSecondary: string;
  toolbarBg: string;
  searchBg: string;
  searchBorder: string;
  searchPlaceholder: string;
  searchIcon: string;
  headerFilterFg: string;
  headerFilterBg: string;
  headerFilterBorder: string;
  headerFilterPlaceholder: string;
  btnBg: string;
  btnBorder: string;
  btnFg: string;
  btnHover: string;
  exportBtnBg: string;
  exportBtnFg: string;
  footerBg: string;
  footerFg: string;
  footerMuted: string;
  footerCountFg: string;
  footerControlBg: string;
  footerControlBorder: string;
}

export interface AccountThemeDefinition extends AccountThemeTokens {
  shell?: Partial<AccountShellTokens>;
  /** When set, overrides generated light/dark table palette for this account group. */
  table?: Partial<CustomTableThemeTokens>;
}

const DEFAULT_THEME: Required<AccountThemeTokens> = {
  primary: '#186391',
  primaryDark: '#124a6b',
  accent: '#D80032',
  primaryRgb: '24, 99, 145',
};

/** My Austin brand palette — black primary (buttons/tables) + orange accent from the logo. */
const AUSTIN_THEME: Required<AccountThemeTokens> = {
  primary: '#000000',
  primaryDark: '#1a1a1a',
  accent: '#F07B20',
  primaryRgb: '0, 0, 0',
};

/** Returns the base theme for the current build environment. */
function getEnvironmentBaseTheme(): Required<AccountThemeTokens> {
  const theme = (environment as any).theme as string | undefined;
  if (theme === 'austin') {
    return AUSTIN_THEME;
  }
  return DEFAULT_THEME;
}

const LIGHT_SHELL: AccountShellTokens = {
  navbarBgStart: '#ffffff',
  navbarBgEnd: '#f8f9fa',
  navbarFg: '#000000',
  navbarFgSecondary: 'rgba(0, 0, 0, 0.6)',
  navbarFgTertiary: 'rgba(0, 0, 0, 0.5)',
  navbarIconMuted: 'rgba(95, 117, 125, 0.9)',
  dropdownBg: '#ffffff',
  dropdownBorder: 'rgba(0, 0, 0, 0.08)',
  dropdownHeaderBorder: 'rgba(0, 0, 0, 0.05)',
  dropdownDivider: 'rgba(0, 0, 0, 0.1)',
  sidebarBg: '#ffffff',
  sidebarText: '#2c3e50',
  sidebarTextMuted: '#6b7280',
  sidebarBorder: '#e5e7eb',
  sidebarHover: '#f3f4f6',
  sidebarActive: '#e5e7eb',
  sidebarIcon: '#9ca3af',
  sidebarSearchBg: '#ffffff',
  sidebarSearchBorder: '#e5e7eb',
  sidebarPopupHeaderBg: '#f9fafb',
};

/** Session row shape for `sessionStorage.accounts` (subset). */
interface SessionAccountRow {
  account_id?: number;
  account_group_id?: number;
  is_approved?: number;
}

/** account_group_id 2: charcoal shell + champagne gold / bronze (shared group branding). */
const GROUP_2_SHELL: Partial<AccountShellTokens> = {
  navbarBgStart: '#1a1a1a',
  navbarBgEnd: '#121212',
  navbarFg: '#ffffff',
  navbarFgSecondary: 'rgba(255, 255, 255, 0.72)',
  navbarFgTertiary: 'rgba(255, 255, 255, 0.55)',
  navbarIconMuted: 'rgba(255, 255, 255, 0.78)',
  dropdownBg: '#1a1a1a',
  dropdownBorder: '#2d2d2d',
  dropdownHeaderBorder: '#2d2d2d',
  dropdownDivider: '#2d2d2d',
  sidebarBg: '#1a1a1a',
  sidebarText: '#f5f5f5',
  sidebarTextMuted: 'rgba(245, 245, 245, 0.62)',
  sidebarBorder: '#2d2d2d',
  sidebarHover: '#2a2a2a',
  sidebarActive: '#333333',
  sidebarIcon: 'rgba(212, 175, 55, 0.88)',
  sidebarSearchBg: '#262626',
  sidebarSearchBorder: '#3d3d3d',
  sidebarPopupHeaderBg: '#252525',
};

/** My Austin shell overrides: orange active sidebar item background. */
const AUSTIN_SHELL: Partial<AccountShellTokens> = {
  sidebarActive: '#FFF0E0',  // warm orange tint — the active item background
};

/** Dark charcoal + gold table palette (reference UI). */
function buildDarkGoldTableTheme(primary: string, primaryDark: string): CustomTableThemeTokens {
  return {
    headerBg: '#2B2929',
    headerFg: '#F5F5F5',
    headerIcon: primary,
    headerDivider: '#3D3A36',
    rowBg: '#ffffff',
    rowAltBg: '#ffffff',
    rowHover: '#f5f5f5',
    rowDivider: '#e0e0e0',
    bodyText: '#000000',
    bodyTextSecondary: '#6b7280',
    toolbarBg: '#1F1E1E',
    searchBg: '#1F1E1E',
    searchBorder: '#3A3733',
    searchPlaceholder: '#8F8A83',
    searchIcon: primary,
    headerFilterFg: '#FFFFFF',
    headerFilterBg: 'rgba(255, 255, 255, 0.12)',
    headerFilterBorder: 'rgba(255, 255, 255, 0.35)',
    headerFilterPlaceholder: 'rgba(255, 255, 255, 0.55)',
    btnBg: '#1F1E1E',
    btnBorder: '#3A3733',
    btnFg: '#F5F5F5',
    btnHover: '#32302F',
    exportBtnBg: primary,
    exportBtnFg: '#FFFFFF',
    footerBg: '#1F1E1E',
    footerFg: primary,
    footerMuted: '#F5F5F5',
    footerCountFg: '#F5F5F5',
    footerControlBg: '#1F1E1E',
    footerControlBorder: '#3A3733',
  };
}

function buildLightTableTheme(primary: string, primaryDark: string): CustomTableThemeTokens {
  return {
    headerBg: primary,
    headerFg: '#ffffff',
    headerIcon: '#ffffff',
    headerDivider: 'rgba(255, 255, 255, 0.28)',
    rowBg: '#ffffff',
    rowAltBg: '#f5f5f5',
    rowHover: '#f3f4f6',
    rowDivider: '#e5e7eb',
    bodyText: '#6b7280',
    bodyTextSecondary: '#9ca3af',
    toolbarBg: '#f3f4f6',
    searchBg: '#ffffff',
    searchBorder: '#e5e7eb',
    searchPlaceholder: '#9e9e9e',
    searchIcon: '#757575',
    headerFilterFg: '#FFFFFF',
    headerFilterBg: 'rgba(255, 255, 255, 0.15)',
    headerFilterBorder: 'rgba(255, 255, 255, 0.35)',
    headerFilterPlaceholder: 'rgba(255, 255, 255, 0.65)',
    btnBg: primary,
    btnBorder: 'rgba(255, 255, 255, 0.35)',
    btnFg: '#ffffff',
    btnHover: primaryDark,
    exportBtnBg: primary,
    exportBtnFg: '#ffffff',
    footerBg: primary,
    footerFg: '#ffffff',
    footerMuted: 'rgba(255, 255, 255, 0.9)',
    footerCountFg: '#FFFFFF',
    footerControlBg: 'rgba(255, 255, 255, 0.15)',
    footerControlBorder: 'rgba(255, 255, 255, 0.35)',
  };
}

/**
 * My Austin table theme:
 * - Header & Buttons: black (#000000)
 * - Accents (export button, footer): orange (#F07B20) from the logo's "my" script
 */
function buildAustinTableTheme(): CustomTableThemeTokens {
  const headerBlack = '#000000';
  const headerBlackDark = '#1a1a1a';
  const accentOrange = '#F07B20';
  const accentOrangeDark = '#CC6010';
  return {
    headerBg: headerBlack,
    headerFg: '#ffffff',
    headerIcon: accentOrange,
    headerDivider: 'rgba(255, 255, 255, 0.20)',
    rowBg: '#ffffff',
    rowAltBg: '#fafafa',
    rowHover: '#fff4ec',
    rowDivider: '#e5e7eb',
    bodyText: '#000000',
    bodyTextSecondary: '#6b7280',
    toolbarBg: '#f5f5f5',
    searchBg: '#ffffff',
    searchBorder: '#e5e7eb',
    searchPlaceholder: '#9e9e9e',
    searchIcon: '#000000',
    headerFilterFg: '#ffffff',
    headerFilterBg: 'rgba(255, 255, 255, 0.12)',
    headerFilterBorder: 'rgba(255, 255, 255, 0.30)',
    headerFilterPlaceholder: 'rgba(255, 255, 255, 0.55)',
    btnBg: headerBlack,
    btnBorder: 'rgba(255, 255, 255, 0.30)',
    btnFg: '#ffffff',
    btnHover: headerBlackDark,
    exportBtnBg: accentOrange,
    exportBtnFg: '#ffffff',
    footerBg: headerBlack,
    footerFg: accentOrange,
    footerMuted: 'rgba(255, 255, 255, 0.85)',
    footerCountFg: '#ffffff',
    footerControlBg: 'rgba(255, 255, 255, 0.12)',
    footerControlBorder: 'rgba(255, 255, 255, 0.28)',
  };
}

const ACCOUNT_GROUP_THEMES: Record<string, AccountThemeDefinition> = {
  '2': {
    primary: '#C8A35A',
    primaryDark: '#A8843F',
    accent: '#D8BE84',
    primaryRgb: '200, 163, 90',
    shell: GROUP_2_SHELL,
  },
};

@Injectable({ providedIn: 'root' })
export class AccountThemeService {
  /** Apply theme from sessionStorage (call on app init and after login). */
  applyFromSession(): void {
    const lookupKey = this.resolveThemeLookupKeyFromSession();
    this.applyThemeForLookupKey(lookupKey);
  }

  /**
   * Re-apply theme after session changes. Uses `account_group_id` from `accounts`
   * for the row matching `account_id`; falls back to default theme if not found.
   */
  applyThemeForLookupKey(lookupKey: string | null | undefined): void {
    // When running under the Austin environment, the Austin palette is always
    // authoritative — never let an account_group_id override it.
    const envTheme = (environment as any).theme as string | undefined;
    if (envTheme === 'austin') {
      lookupKey = null;
    }
    const themeKey = lookupKey?.trim() ?? '';
    const merged = this.resolveTheme(lookupKey);
    const shell = this.mergeShell(lookupKey);
    const root = document.documentElement;
    const primaryRgb = merged.primaryRgb ?? this.hexToRgbTriplet(merged.primary);

    root.style.setProperty('--app-primary', merged.primary);
    root.style.setProperty('--app-primary-dark', merged.primaryDark);
    root.style.setProperty('--app-accent', merged.accent);
    root.style.setProperty('--app-primary-rgb', primaryRgb);

    root.style.setProperty('--mat-table-header-bg-color', merged.primary);
    root.style.setProperty('--mat-table-header-text-color', '#ffffff');

    root.style.setProperty('--app-custom-table-primary', merged.primary);
    root.style.setProperty('--app-custom-table-primary-dark', merged.primaryDark);
    root.style.setProperty('--app-custom-table-accent', merged.accent);
    root.style.setProperty('--app-custom-table-primary-rgb', primaryRgb);

    const groupTheme = themeKey ? ACCOUNT_GROUP_THEMES[themeKey] : undefined;
    let baseTable: CustomTableThemeTokens;
    if (envTheme === 'austin') {
      // Austin: charcoal grey header from the logo, orange accents
      baseTable = buildAustinTableTheme();
    } else if (groupTheme) {
      baseTable = buildDarkGoldTableTheme(merged.primary, merged.primaryDark);
    } else {
      baseTable = buildLightTableTheme(merged.primary, merged.primaryDark);
    }
    const table = { ...baseTable, ...groupTheme?.table };
    this.applyCustomTableTheme(root, table);

    root.style.setProperty('--mat-table-header-bg-color', table.headerBg);
    root.style.setProperty('--mat-table-header-text-color', table.headerFg);

    root.style.setProperty('--mdc-protected-button-container-color', merged.primary);

    root.style.setProperty('--mdc-chip-elevated-container-color', merged.primary);
    root.style.setProperty('--mdc-chip-elevated-selected-container-color', merged.primary);
    root.style.setProperty('--mdc-chip-elevated-disabled-container-color', merged.primary);
    root.style.setProperty('--mdc-chip-flat-disabled-selected-container-color', merged.primary);

    this.applyShell(root, shell);

    // Austin: orange left-bar & orange icon on active sidebar item; reset for other themes.
    if (envTheme === 'austin') {
      root.style.setProperty('--app-sidebar-active-accent', '#F07B20');
      root.style.setProperty('--app-sidebar-active-icon', '#F07B20');
    } else {
      root.style.removeProperty('--app-sidebar-active-accent');
      root.style.removeProperty('--app-sidebar-active-icon');
    }
  }

  /**
   * Reads `accounts` JSON, finds the row for the current `account_id`,
   * returns `String(account_group_id)` when present (used as theme map key).
   */
  private resolveThemeLookupKeyFromSession(): string | null {
    const accountId = sessionStorage.getItem('account_id');
    const raw = sessionStorage.getItem('accounts');
    if (!raw) {
      return null;
    }
    try {
      const accounts = JSON.parse(raw) as SessionAccountRow[];
      if (!Array.isArray(accounts) || accounts.length === 0) {
        return null;
      }
      const match =
        accountId != null && accountId !== ''
          ? accounts.find((a) => String(a.account_id) === String(accountId))
          : undefined;
      const row =
        match ?? accounts.find((a) => a.is_approved === 1) ?? accounts[0];
      if (row?.account_group_id != null && String(row.account_group_id).trim() !== '') {
        return String(row.account_group_id);
      }
    } catch {
      /* ignore invalid JSON */
    }
    return null;
  }

  private applyCustomTableTheme(root: HTMLElement, table: CustomTableThemeTokens): void {
    root.style.setProperty('--app-custom-table-header-bg', table.headerBg);
    root.style.setProperty('--app-custom-table-header-fg', table.headerFg);
    root.style.setProperty('--app-custom-table-header-icon', table.headerIcon);
    root.style.setProperty('--app-custom-table-header-divider', table.headerDivider);
    root.style.setProperty('--app-custom-table-header-muted', table.bodyTextSecondary);
    root.style.setProperty('--app-custom-table-toolbar-bg', table.toolbarBg);
    root.style.setProperty('--app-custom-table-row-bg', table.rowBg);
    root.style.setProperty('--app-custom-table-row-alt-bg', table.rowAltBg);
    root.style.setProperty('--app-custom-table-row-hover', table.rowHover);
    root.style.setProperty('--app-custom-table-row-divider', table.rowDivider);
    root.style.setProperty('--app-custom-table-border', table.rowDivider);
    root.style.setProperty('--app-custom-table-body-text', table.bodyText);
    root.style.setProperty('--app-custom-table-text-muted', table.bodyTextSecondary);
    root.style.setProperty('--app-custom-table-search-bg', table.searchBg);
    root.style.setProperty('--app-custom-table-search-border', table.searchBorder);
    root.style.setProperty('--app-custom-table-search-placeholder', table.searchPlaceholder);
    root.style.setProperty('--app-custom-table-search-icon', table.searchIcon);
    root.style.setProperty('--app-custom-table-header-filter-fg', table.headerFilterFg);
    root.style.setProperty('--app-custom-table-header-filter-bg', table.headerFilterBg);
    root.style.setProperty('--app-custom-table-header-filter-border', table.headerFilterBorder);
    root.style.setProperty('--app-custom-table-header-filter-placeholder', table.headerFilterPlaceholder);
    root.style.setProperty('--app-custom-table-btn-bg', table.btnBg);
    root.style.setProperty('--app-custom-table-btn-border', table.btnBorder);
    root.style.setProperty('--app-custom-table-btn-fg', table.btnFg);
    root.style.setProperty('--app-custom-table-btn-hover', table.btnHover);
    root.style.setProperty('--app-custom-table-export-btn-bg', table.exportBtnBg);
    root.style.setProperty('--app-custom-table-export-btn-fg', table.exportBtnFg);
    root.style.setProperty('--app-custom-table-footer-bg', table.footerBg);
    root.style.setProperty('--app-custom-table-footer-fg', table.footerFg);
    root.style.setProperty('--app-custom-table-footer-muted', table.footerMuted);
    root.style.setProperty('--app-custom-table-footer-count-fg', table.footerCountFg);
    root.style.setProperty('--app-custom-table-footer-control-bg', table.footerControlBg);
    root.style.setProperty('--app-custom-table-footer-control-border', table.footerControlBorder);
  }

  private applyShell(root: HTMLElement, shell: AccountShellTokens): void {
    root.style.setProperty('--app-navbar-bg-start', shell.navbarBgStart);
    root.style.setProperty('--app-navbar-bg-end', shell.navbarBgEnd);
    root.style.setProperty('--app-navbar-fg', shell.navbarFg);
    root.style.setProperty('--app-navbar-fg-secondary', shell.navbarFgSecondary);
    root.style.setProperty('--app-navbar-fg-tertiary', shell.navbarFgTertiary);
    root.style.setProperty('--app-navbar-icon-muted', shell.navbarIconMuted);
    root.style.setProperty('--app-dropdown-bg', shell.dropdownBg);
    root.style.setProperty('--app-dropdown-border', shell.dropdownBorder);
    root.style.setProperty('--app-dropdown-header-border', shell.dropdownHeaderBorder);
    root.style.setProperty('--app-dropdown-divider', shell.dropdownDivider);
    root.style.setProperty('--app-sidebar-bg', shell.sidebarBg);
    root.style.setProperty('--app-sidebar-text', shell.sidebarText);
    root.style.setProperty('--app-sidebar-text-muted', shell.sidebarTextMuted);
    root.style.setProperty('--app-sidebar-border', shell.sidebarBorder);
    root.style.setProperty('--app-sidebar-hover', shell.sidebarHover);
    root.style.setProperty('--app-sidebar-active', shell.sidebarActive);
    root.style.setProperty('--app-sidebar-icon', shell.sidebarIcon);
    root.style.setProperty('--app-sidebar-search-bg', shell.sidebarSearchBg);
    root.style.setProperty('--app-sidebar-search-border', shell.sidebarSearchBorder);
    root.style.setProperty('--app-sidebar-popup-header-bg', shell.sidebarPopupHeaderBg);
  }

  private mergeShell(lookupKey: string | null | undefined): AccountShellTokens {
    const key = lookupKey?.trim() ?? '';
    const partial = key ? ACCOUNT_GROUP_THEMES[key]?.shell : undefined;
    const envTheme = (environment as any).theme as string | undefined;
    const envShell = envTheme === 'austin' ? AUSTIN_SHELL : undefined;
    return { ...LIGHT_SHELL, ...envShell, ...partial };
  }

  private resolveTheme(lookupKey: string | null | undefined): Required<AccountThemeTokens> {
    const key = lookupKey?.trim() ?? '';
    const override = key ? ACCOUNT_GROUP_THEMES[key] : undefined;
    const base = getEnvironmentBaseTheme();
    const primary = override?.primary ?? base.primary;
    return {
      primary,
      primaryDark: override?.primaryDark ?? base.primaryDark,
      accent: override?.accent ?? base.accent,
      primaryRgb: override?.primaryRgb ?? this.hexToRgbTriplet(primary),
    };
  }

  private hexToRgbTriplet(hex: string): string {
    const h = hex.replace('#', '').trim();
    if (h.length !== 6 || Number.isNaN(parseInt(h, 16))) {
      return getEnvironmentBaseTheme().primaryRgb;
    }
    const r = parseInt(h.slice(0, 2), 16);
    const g = parseInt(h.slice(2, 4), 16);
    const b = parseInt(h.slice(4, 6), 16);
    return `${r}, ${g}, ${b}`;
  }
}
